import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CircleDollarSign, Info, Receipt, ShoppingCart, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { RodapeDeContagem, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarCompetencia, formatarDecimal, formatarDia, formatarReais } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { useSessao } from '@/modulos/sessao/consultas';

import { apiDoOrcamento, type LinhaDoDesvio, type Premissas, type PrevistoRealizado as Dados } from './api';
import { chaveDaConsultaDosDesvios, competenciaDe, detalheDaExplicacao, fraseDoDesvio, percentualEscrito, reaisComSinal, somarMeses, ultimoMesFechado } from './orcamento';
import { SeloDoOrcamento } from './Selo';

/**
 * FERRAMENTA 2 DO MÓDULO 19 — previsto × comprometido × realizado, por centro
 * e conta, no mês escolhido (o padrão é o último mês fechado), com o
 * acumulado do ano no painel. Cada valor leva aos registros que o formam
 * (guia §9): as linhas do orçamento, os pedidos em aberto e a DRE do 15. O
 * desvio relevante sem explicação pede uma, e a explicação fica na história.
 */
export function PrevistoRealizado({
  versaoId,
  competencia: escolhida,
  aoEscolherCompetencia,
  aberta,
  aoAbrir,
}: {
  readonly versaoId: string | null;
  readonly competencia: string | null;
  readonly aoEscolherCompetencia: (c: string) => void;
  readonly aberta: string | null;
  readonly aoAbrir: (chave: string) => void;
}) {
  const competencia = escolhida ? `${escolhida.slice(0, 7)}-01` : ultimoMesFechado();
  const consulta = useQuery({
    queryKey: chaveDaConsultaDosDesvios(competencia, versaoId),
    queryFn: () => apiDoOrcamento.previstoRealizado(competencia, versaoId ?? undefined),
    meta: { oQueLia: 'o previsto e realizado' },
  });
  const d = consulta.data;
  const corrente = competenciaDe(new Date());
  const meses = Array.from({ length: 18 }, (_, i) => somarMeses(corrente, -i));

  return (
    <>
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-56">
          <CampoSelecao
            rotulo="Competência"
            valor={competencia.slice(0, 7)}
            aoMudar={(c) => { aoEscolherCompetencia(`${c}-01`); }}
            opcoes={meses.map((c) => ({ valor: c.slice(0, 7), rotulo: `${formatarCompetencia(c)}${c === corrente ? ' (em andamento)' : ''}` }))}
          />
        </div>
        <p className="txt-apoio min-w-0 flex-1 text-pretty text-tinta-secundaria">
          {d
            ? d.versao
              ? `Contra o orçamento ${String(d.versao.exercicio)}, versão ${String(d.versao.versao)} (aprovada). Desvio relevante: a partir de ${formatarReais(d.limiares.valor)} e ${formatarDecimal(d.limiares.pct, 1)}% do previsto${d.limiares.padrao ? ', os limiares recomendados' : ''}.`
              : `O exercício de ${competencia.slice(0, 4)} não tem orçamento aprovado: o previsto fica em branco.`
            : 'O previsto da versão aprovada contra o realizado da DRE, com o que está pedido.'}
        </p>
      </div>

      {consulta.isError && !d ? (
        <ErroDaLista oQue="O previsto e realizado" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
      ) : (
        <>
          <FaixaIndicadores>
            <CardIndicador
              rotulo="Receita realizada"
              icone={Receipt}
              tom="marca"
              carregando={!d}
              valor={d ? formatarReais(d.totais.receita.realizado) : ''}
              contexto={d ? `Previsto ${formatarReais(d.totais.receita.previsto)} · ${formatarCompetencia(d.competencia)}` : 'Previsto'}
            />
            <CardIndicador
              rotulo="Gasto realizado e comprometido"
              icone={CircleDollarSign}
              tom="neutro"
              carregando={!d}
              valor={d ? formatarReais(d.totais.gasto.realizado + d.totais.gasto.comprometido) : ''}
              alerta={(d?.totais.gasto.saldo ?? 0) < 0}
              contexto={d ? `Previsto ${formatarReais(d.totais.gasto.previsto)} · saldo ${reaisComSinal(d.totais.gasto.saldo)}` : 'Previsto'}
            />
            <CardIndicador
              rotulo="Comprometido em pedidos"
              icone={ShoppingCart}
              tom="disponivel"
              carregando={!d}
              valor={d ? formatarReais(d.totais.gasto.comprometido) : ''}
              contexto="Aprovado ou emitido, ainda não chegou"
            />
            <CardIndicador
              rotulo="Desvios sem explicação"
              icone={TriangleAlert}
              tom="standby"
              carregando={!d}
              valor={d ? String(d.resumo.semExplicacao) : ''}
              alerta={(d?.resumo.semExplicacao ?? 0) > 0}
              contexto={d ? `${String(d.resumo.relevantes)} ${d.resumo.relevantes === 1 ? 'desvio relevante' : 'desvios relevantes'} no mês` : 'Desvios relevantes'}
            />
          </FaixaIndicadores>
          {d?.emAndamento ? (
            <p className="txt-apoio flex items-start gap-2 text-pretty text-atencao-texto">
              <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
              O mês ainda está em andamento: o realizado é parcial, e só o desvio para o lado ruim pede explicação.
            </p>
          ) : null}

          <TabelaDosDesvios linhas={d?.linhas ?? []} carregando={consulta.isPending} aberta={aberta} aoAbrir={aoAbrir} temVersao={d ? d.versao !== null : true} />

          <PremissasDoNumero titulo="De onde vem cada coluna" premissas={d?.premissas} />
        </>
      )}
    </>
  );
}

function TabelaDosDesvios({
  linhas,
  carregando,
  aberta,
  aoAbrir,
  temVersao,
}: {
  readonly linhas: readonly LinhaDoDesvio[];
  readonly carregando: boolean;
  readonly aberta: string | null;
  readonly aoAbrir: (chave: string) => void;
  readonly temVersao: boolean;
}) {
  const [pagina, setPagina] = useState(1);
  const porPagina = 20;
  const paginas = Math.max(1, Math.ceil(linhas.length / porPagina));
  const atual = Math.min(pagina, paginas);
  /* Quem pede explicação primeiro: é o que a tela existe para mostrar. */
  const ordenadas = [...linhas].sort((a, b) => Number(b.precisaExplicar) - Number(a.precisaExplicar) || Number(b.relevante) - Number(a.relevante) || Math.abs(b.desvio) - Math.abs(a.desvio));
  const visiveis = ordenadas.slice((atual - 1) * porPagina, atual * porPagina);
  const reais = (v: number, alerta = false) => <span className={`numero whitespace-nowrap ${alerta && v < 0 ? 'text-parada-texto' : 'text-tinta'}`}>{reaisComSinal(v)}</span>;
  const colunas: readonly Coluna<LinhaDoDesvio>[] = [
    {
      chave: 'linha',
      rotulo: 'Centro e conta',
      prioridade: 'chave',
      celula: (l) => (
        <span className="flex min-w-0 flex-col">
          <span className="txt-corpo text-tinta">
            <span className="codigo">{l.centro?.codigo ?? 'Sem centro'}</span>
            {` · ${l.conta.codigo ?? ''} ${l.conta.nome}`}
          </span>
          <TextoDaCelula texto={l.centro?.nome ?? 'Pedido sem O.S. de origem'} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    { chave: 'previsto', rotulo: 'Previsto', alinhamento: 'numero', celula: (l) => reais(l.previsto) },
    { chave: 'comprometido', rotulo: 'Comprometido', alinhamento: 'numero', prioridade: 'secundaria', celula: (l) => reais(l.comprometido) },
    { chave: 'realizado', rotulo: 'Realizado', alinhamento: 'numero', celula: (l) => reais(l.realizado) },
    { chave: 'saldo', rotulo: 'Saldo', alinhamento: 'numero', prioridade: 'secundaria', celula: (l) => (l.natureza === 'receita' ? <span className="txt-apoio text-tinta-apagada">—</span> : reais(l.saldo, true)) },
    {
      chave: 'desvio',
      rotulo: 'Desvio',
      alinhamento: 'numero',
      celula: (l) => (
        <span className="flex flex-col items-end">
          <span className={`numero whitespace-nowrap ${l.sentido === 'pior' ? 'text-parada-texto' : l.sentido === 'melhor' ? 'text-operando-texto' : 'text-tinta'}`}>{reaisComSinal(l.desvio)}</span>
          <span className="txt-apoio whitespace-nowrap text-tinta-apagada">{percentualEscrito(l.desvioPct, true)}</span>
        </span>
      ),
    },
    {
      chave: 'situacao',
      rotulo: 'Explicação',
      alinhamento: 'status',
      celula: (l) =>
        l.precisaExplicar ? <SeloDoOrcamento rotulo="Pede explicação" tom="atencao" />
          : l.explicacao ? <SeloDoOrcamento rotulo="Explicado" tom="sucesso" />
            : <span className="txt-apoio text-tinta-apagada">{l.relevante ? '—' : 'Dentro do limiar'}</span>,
    },
  ];
  return (
    <Tabela
      rotulo="Previsto, comprometido e realizado por centro e conta"
      colunas={colunas}
      linhas={visiveis}
      chaveDaLinha={(l) => l.chave}
      carregando={carregando}
      alturaDupla
      selecionada={aberta}
      aoSelecionar={(l) => { aoAbrir(l.chave); }}
      vazio={
        <EstadoVazio
          titulo="Nada previsto nem realizado no mês"
          descricao={temVersao
            ? 'Sem linha do orçamento, sem pedido e sem custo ou receita apurados nesta competência. Escolha outro mês.'
            : 'Sem orçamento aprovado e sem realizado nesta competência. Aprove a versão do exercício, ou escolha outro mês.'}
        />
      }
      rodape={
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <RodapeDeContagem total={linhas.length} />
          {paginas > 1 ? (
            <span className="flex gap-2">
              <Botao nivel="fantasma" tamanho="p" disabled={atual <= 1} onClick={() => { setPagina(atual - 1); }}>Página anterior</Botao>
              <Botao nivel="fantasma" tamanho="p" disabled={atual >= paginas} onClick={() => { setPagina(atual + 1); }}>Próxima página</Botao>
            </span>
          ) : null}
        </div>
      }
    />
  );
}

/** O painel de uma linha do previsto × realizado: o acumulado, de onde vem cada valor e a explicação. */
export function PainelDoDesvio({ chave, competencia: escolhida, versaoId, aoFechar }: { readonly chave: string; readonly competencia: string | null; readonly versaoId: string | null; readonly aoFechar: () => void }) {
  const competencia = escolhida ? `${escolhida.slice(0, 7)}-01` : ultimoMesFechado();
  const consulta = useQuery({
    queryKey: chaveDaConsultaDosDesvios(competencia, versaoId),
    queryFn: () => apiDoOrcamento.previstoRealizado(competencia, versaoId ?? undefined),
  });
  const sessao = useSessao();
  const explica = (sessao.data?.permissoes ?? []).includes('desvio.explicar');
  const d: Dados | undefined = consulta.data;
  const l = d?.linhas.find((x) => x.chave === chave);
  if (!d || !l) return null;
  const titulo = `${l.conta.codigo ?? ''} ${l.conta.nome}`.trim();
  return (
    <PainelDetalhe
      codigo={l.centro?.codigo ?? 'SEM CENTRO'}
      titulo={titulo}
      subtitulo={`${l.centro?.nome ?? 'Pedido sem O.S. de origem'} · ${formatarCompetencia(d.competencia)}`}
      marcadores={l.precisaExplicar ? <SeloDoOrcamento rotulo="Pede explicação" tom="atencao" /> : l.explicacao ? <SeloDoOrcamento rotulo="Explicado" tom="sucesso" /> : null}
      dadosChave={
        <>
          <DadoChave rotulo="Previsto no mês">{formatarReais(l.previsto)}</DadoChave>
          <DadoChave rotulo="Realizado no mês">{formatarReais(l.realizado)}</DadoChave>
          <DadoChave rotulo="Comprometido">{formatarReais(l.comprometido)}</DadoChave>
          <DadoChave rotulo="Desvio">{`${reaisComSinal(l.desvio)} · ${fraseDoDesvio(l)}`}</DadoChave>
          <DadoChave rotulo="Previsto no ano">{formatarReais(l.acumulado.previsto)}</DadoChave>
          <DadoChave rotulo="Realizado no ano">{`${formatarReais(l.acumulado.realizado)} · ${percentualEscrito(l.acumulado.desvioPct, true)}`}</DadoChave>
        </>
      }
      abas={[
        {
          id: 'origem',
          rotulo: 'De onde vem',
          conteudo: (
            <div className="flex flex-col gap-4">
              <section className="flex flex-col gap-2" aria-label="Linhas do orçamento">
                <h3 className="txt-corpo font-semibold text-tinta">Previsto: as linhas do orçamento</h3>
                {l.origem.linhas.length === 0 ? (
                  <p className="txt-apoio text-tinta-secundaria">Nenhuma linha do orçamento neste centro e conta no mês.</p>
                ) : (
                  <ul className="flex flex-col divide-y divide-borda-sutil">
                    {l.origem.linhas.map((x) => (
                      <li key={x.id} className="flex items-baseline justify-between gap-3 py-2">
                        <span className="txt-apoio min-w-0 text-tinta">{x.memoria}</span>
                        <span className="numero shrink-0 whitespace-nowrap text-tinta">{formatarReais(x.valor)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <section className="flex flex-col gap-2" aria-label="Pedidos em aberto">
                <h3 className="txt-corpo font-semibold text-tinta">Comprometido: os pedidos que ainda não chegaram</h3>
                {l.origem.pedidos.length === 0 ? (
                  <p className="txt-apoio text-tinta-secundaria">Nenhum pedido aprovado ou emitido com entrega neste mês.</p>
                ) : (
                  <ul className="flex flex-col divide-y divide-borda-sutil">
                    {l.origem.pedidos.map((p) => (
                      <li key={`${p.numero}-${p.produto}`} className="flex items-baseline justify-between gap-3 py-2">
                        <span className="txt-apoio min-w-0 text-tinta">{`${p.numero} · ${p.fornecedor} · ${p.produto} · faltam ${formatarDecimal(p.pendente, 1).replace(/,0$/, '')} · entrega ${formatarDia(p.entregaPrevista)}`}</span>
                        <span className="numero shrink-0 whitespace-nowrap text-tinta">{formatarReais(p.valor)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <section className="flex flex-col gap-2" aria-label="Realizado">
                <h3 className="txt-corpo font-semibold text-tinta">Realizado: a DRE gerencial do centro</h3>
                <p className="txt-apoio text-pretty text-tinta-secundaria">O realizado é a DRE do financeiro por centro de custo, na mesma conta. Lá cada linha abre de que máquina e origem saiu.</p>
                {l.centro ? (
                  <Link className="txt-corpo text-marca underline" to="/financeiro" search={{ recorte: 'centro', meses: 1, ate: d.competencia.slice(0, 7) }}>
                    Abrir a DRE por centro de {formatarCompetencia(d.competencia)}
                  </Link>
                ) : null}
              </section>
              {l.decomposicao ? (
                <section className="flex flex-col gap-2" aria-label="Preço e volume">
                  <h3 className="txt-corpo font-semibold text-tinta">Preço e volume</h3>
                  <p className="txt-apoio text-pretty text-tinta">
                    {`Previstas ${formatarDecimal(l.decomposicao.quantidadePrevista, 1)} h a ${formatarReais(l.decomposicao.precoPrevisto)}/h; reconhecidas ${formatarDecimal(l.decomposicao.quantidadeRealizada, 1)} h a ${formatarReais(l.decomposicao.precoRealizado)}/h. Efeito volume ${reaisComSinal(l.decomposicao.efeitoVolume)}; efeito preço ${reaisComSinal(l.decomposicao.efeitoPreco)}.`}
                  </p>
                </section>
              ) : null}
            </div>
          ),
        },
        {
          id: 'explicacao',
          rotulo: 'Explicação',
          conteudo: <ExplicacaoDoDesvio linha={l} dados={d} explica={explica} />,
        },
      ]}
      aoFechar={aoFechar}
    />
  );
}

function ExplicacaoDoDesvio({ linha: l, dados: d, explica }: { readonly linha: LinhaDoDesvio; readonly dados: Dados; readonly explica: boolean }) {
  const cliente = useQueryClient();
  const [texto, setTexto] = useState('');
  const falta = texto.trim().length < 15 ? 'Explique com pelo menos 15 caracteres, por exemplo "troca de material rodante não prevista na ESC-004".' : null;
  const explicar = useMutation({
    mutationFn: () => apiDoOrcamento.explicar({ versaoId: d.versao?.id ?? '', competencia: d.competencia, centroCustoId: l.centro?.id ?? '', contaId: l.conta.id, texto }),
    meta: { retorno: retornoDe<{ competencia: string; centro: string | null; conta: string | null; nomeDaConta: string | null; texto: string }>((r) => ({ titulo: 'Desvio explicado', ...detalheDaExplicacao(r) })) },
    onSuccess: async () => {
      setTexto('');
      await cliente.invalidateQueries({ queryKey: ['orcamento', 'desvios'] });
    },
  });
  return (
    <div className="flex flex-col gap-4">
      {l.explicacao ? (
        <blockquote className="bloco-rebaixado flex flex-col gap-1 rounded-controle p-4">
          <span className="txt-corpo text-pretty text-tinta">{l.explicacao.texto}</span>
          <span className="txt-apoio text-tinta-secundaria">{`${l.explicacao.autor ?? 'Quem saiu da empresa'} · ${formatarDia(l.explicacao.em)}`}</span>
        </blockquote>
      ) : (
        <p className="txt-apoio text-pretty text-tinta-secundaria">
          {l.relevante ? 'Este desvio passou dos limiares e ainda não tem explicação.' : 'Este desvio está dentro dos limiares: explicar é opcional.'}
        </p>
      )}
      {!d.versao || !l.centro ? (
        <p className="txt-apoio text-pretty text-tinta-secundaria">A explicação é de um centro e conta de uma versão aprovada.</p>
      ) : explica ? (
        <>
          {explicar.isError ? <p role="alert" className="txt-apoio text-parada-texto">{`${explicarFalha(explicar.error).erro}. ${explicarFalha(explicar.error).comoResolver}`}</p> : null}
          <CampoTexto
            rotulo={l.explicacao ? 'Nova explicação' : 'Explicação'}
            valor={texto}
            aoMudar={setTexto}
            placeholder="Troca de material rodante não prevista na ESC-004"
            ajuda="Fica na história do desvio, com o seu nome; a anterior não se apaga."
            validar={() => (texto.length > 0 ? falta : null)}
            maxLength={600}
          />
          <Botao nivel="primario" className="self-end" disabled={falta !== null || explicar.isPending} onClick={() => { explicar.mutate(); }}>
            {explicar.isPending ? 'Gravando…' : 'Explicar desvio'}
          </Botao>
        </>
      ) : (
        <p className="txt-apoio text-pretty text-tinta-secundaria">Explicar o desvio pede o perfil Controladoria.</p>
      )}
    </div>
  );
}

/** As premissas que viajam com o número: o que entrou e o que ficou fora. */
export function PremissasDoNumero({ titulo, premissas, frase }: { readonly titulo: string; readonly premissas: Premissas | undefined; readonly frase?: string | undefined }) {
  if (!premissas) {
    return (
      <Carregando oQue="Carregando as premissas" className="flex flex-col gap-2 py-1">
        <Barra className="h-3 w-full" />
        <Barra className="h-3 w-2/5" />
      </Carregando>
    );
  }
  return (
    <section className="cartao flex flex-col gap-4 p-6" aria-label={titulo}>
      {frase ? (
        <div className="flex items-start gap-3">
          <Info aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-disponivel" />
          <p className="txt-corpo text-pretty text-tinta">{frase}</p>
        </div>
      ) : null}
      <h2 className="txt-secao text-tinta">{titulo}</h2>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="flex flex-col gap-2">
          <h3 className="txt-corpo font-semibold text-tinta">O que entrou</h3>
          <ul className="txt-apoio flex list-disc flex-col gap-1 pl-5 text-pretty text-tinta-secundaria">
            {premissas.inclui.map((f) => <li key={f}>{f}</li>)}
          </ul>
        </div>
        <div className="flex flex-col gap-2">
          <h3 className="txt-corpo font-semibold text-tinta">{premissas.completo ? 'Nada ficou fora' : 'O que ficou fora'}</h3>
          <ul className="txt-apoio flex list-disc flex-col gap-1 pl-5 text-pretty text-tinta-secundaria">
            {premissas.naoInclui.map((f) => <li key={f}>{f}</li>)}
          </ul>
        </div>
      </div>
    </section>
  );
}
